/**
 * /privacy — Privacy Policy
 *
 * Publicly accessible, no authentication required.
 * Compliant with BC PIPA (Personal Information Protection Act)
 * and informed by GDPR principles for international users.
 *
 * ⚠️  LEGAL NOTICE: This draft has been prepared for review purposes.
 *     Have qualified legal counsel in British Columbia review and
 *     approve this document before public launch.
 */
import { Link } from "react-router-dom";
import { KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PublicFooter } from "@/components/PublicFooter";

const EFFECTIVE_DATE = "March 5, 2026";
const COMPANY_NAME = "Coastline Digital Solutions";
const COMPANY_SHORT = "Stratakey";
const PRIVACY_EMAIL = "info@stratakey.ca";

export default function PrivacyPolicy() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Nav bar */}
      <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/95 px-6 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
            <KeyRound className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-sm">{COMPANY_SHORT}</span>
        </Link>
        <Separator orientation="vertical" className="h-4" />
        <span className="text-sm text-muted-foreground">Privacy Policy</span>
        <div className="ml-auto">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-10">
        <div className="mx-auto max-w-3xl px-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </div>

          <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none space-y-8 text-sm leading-relaxed text-foreground">

            {/* Intro */}
            <section>
              <p>
                {COMPANY_NAME} ("<strong>Stratakey</strong>," "<strong>we</strong>," "<strong>us</strong>")
                respects your privacy. This Privacy Policy describes how we collect, use, share,
                and protect personal information when you use the Stratakey STR Compliance Platform
                (the "<strong>Service</strong>"). We comply with the{" "}
                <em>BC Personal Information Protection Act</em> (PIPA, SBC 2003, c. 63) and
                applicable Canadian federal privacy law.
              </p>
            </section>

            <Separator />

            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">1. Who We Are</h2>
              <p>
                {COMPANY_NAME} is a British Columbia corporation that operates the Stratakey
                platform — a cloud-based tool helping strata corporations and short-term rental
                hosts manage regulatory compliance under the BC Short-Term Rental Accommodations Act.
              </p>
              <p className="mt-3">
                <strong>Privacy contact:</strong>{" "}
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary underline">{PRIVACY_EMAIL}</a>
              </p>
            </section>

            <Separator />

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">2. Personal Information We Collect</h2>
              <p>We collect personal information in the following categories:</p>

              <div className="mt-4 space-y-4">
                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-sm mb-2">Account &amp; Profile Data</h3>
                  <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                    <li>Full name, email address, phone number (optional)</li>
                    <li>Role (strata admin, property manager, host)</li>
                    <li>Account creation date and last login time</li>
                  </ul>
                </div>

                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-sm mb-2">Organization &amp; STR Data</h3>
                  <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                    <li>Strata corporation name and address</li>
                    <li>Unit numbers and host registration details</li>
                    <li>BC STR permit registration numbers (validated via BC Registry API)</li>
                    <li>Booking records including guest name, check-in/check-out dates, guest count</li>
                    <li>Guest government-issued ID images (uploaded during check-in)</li>
                  </ul>
                </div>

                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-sm mb-2">Payment Data</h3>
                  <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                    <li>Payment card type and last 4 digits (from Stripe — we never see full card numbers)</li>
                    <li>Billing history and subscription status</li>
                  </ul>
                </div>

                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-sm mb-2">Usage &amp; Analytics Data</h3>
                  <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                    <li>Feature usage events (e.g., booking created, host approved) — collected via PostHog</li>
                    <li>Session frequency and last-seen timestamps</li>
                    <li>Error reports (via Sentry — no personally identifiable content)</li>
                    <li>We do <strong>not</strong> collect IP addresses or enable session recordings</li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator />

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">3. How We Use Personal Information</h2>
              <p>We use your personal information only for the following purposes:</p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li><strong>Service delivery:</strong> Providing, maintaining, and improving the STR compliance platform</li>
                <li><strong>Regulatory compliance:</strong> Enabling strata corporations to maintain records required under BC law</li>
                <li><strong>Communication:</strong> Sending transactional emails (booking confirmations, daily digest summaries, account alerts) via our email provider (Resend)</li>
                <li><strong>Billing:</strong> Processing subscription payments via Stripe</li>
                <li><strong>Product analytics:</strong> Understanding how the Service is used in aggregate to improve features (via PostHog — role and org ID only, no name or email)</li>
                <li><strong>Security &amp; error monitoring:</strong> Diagnosing and fixing technical errors (via Sentry — stack traces and user role only)</li>
              </ul>
              <p className="mt-3">
                We do <strong>not</strong> use your data for advertising, profiling for
                third-party commercial purposes, or automated decision-making that has legal effects on you.
              </p>
            </section>

            <Separator />

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">4. How We Share Personal Information</h2>
              <p>
                We do <strong>not</strong> sell, rent, or trade your personal information.
                We share personal information only with the following categories of third-party
                service providers ("<strong>Sub-processors</strong>") who process data on our behalf:
              </p>
              <div className="mt-4 overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium">Provider</th>
                      <th className="text-left px-4 py-2.5 font-medium">Purpose</th>
                      <th className="text-left px-4 py-2.5 font-medium">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="px-4 py-3 font-medium">Supabase / AWS</td>
                      <td className="px-4 py-3 text-muted-foreground">Database, authentication, file storage</td>
                      <td className="px-4 py-3 text-muted-foreground">US (us-east-1)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Stripe</td>
                      <td className="px-4 py-3 text-muted-foreground">Payment processing</td>
                      <td className="px-4 py-3 text-muted-foreground">US</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Resend</td>
                      <td className="px-4 py-3 text-muted-foreground">Transactional email delivery</td>
                      <td className="px-4 py-3 text-muted-foreground">US</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">PostHog</td>
                      <td className="px-4 py-3 text-muted-foreground">Product analytics (anonymized usage events)</td>
                      <td className="px-4 py-3 text-muted-foreground">US / EU</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Sentry</td>
                      <td className="px-4 py-3 text-muted-foreground">Error monitoring (stack traces, no PII)</td>
                      <td className="px-4 py-3 text-muted-foreground">US</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">BC Registries API</td>
                      <td className="px-4 py-3 text-muted-foreground">STR permit number validation</td>
                      <td className="px-4 py-3 text-muted-foreground">Canada (BC)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Cloudflare Pages</td>
                      <td className="px-4 py-3 text-muted-foreground">Web application hosting (CDN)</td>
                      <td className="px-4 py-3 text-muted-foreground">US</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                We may also disclose personal information if required by law, court order, or
                regulatory authority, or to protect the rights, property, or safety of {COMPANY_SHORT},
                our users, or the public.
              </p>
            </section>

            <Separator />

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">5. Cookies and Tracking</h2>
              <p>
                The Service uses the following types of cookies and local storage:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li>
                  <strong>Essential cookies:</strong> Supabase authentication tokens stored in
                  browser local storage. Required for the Service to function.
                </li>
                <li>
                  <strong>Analytics cookies:</strong> PostHog uses a first-party cookie to
                  distinguish unique sessions. We capture only role and organization identifiers
                  — never name or email. IP addresses are not captured.
                </li>
                <li>
                  <strong>Preference storage:</strong> Your selected active organization is stored
                  in session storage and cleared when you close the browser.
                </li>
              </ul>
              <p className="mt-3">
                We do <strong>not</strong> use third-party advertising cookies or cross-site tracking.
                You may clear cookies at any time through your browser settings, but this will
                sign you out of the Service.
              </p>
            </section>

            <Separator />

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">6. Data Retention</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Active accounts:</strong> Data is retained for as long as your account
                  is active and for 90 days after closure.
                </li>
                <li>
                  <strong>Booking and compliance records:</strong> Retained for 7 years to meet
                  potential regulatory and legal obligations under BC law.
                </li>
                <li>
                  <strong>Guest ID images:</strong> Retained for the duration of the booking plus
                  90 days, then permanently deleted from storage.
                </li>
                <li>
                  <strong>Analytics data:</strong> Retained in PostHog for 12 months in accordance
                  with their data retention policy.
                </li>
                <li>
                  <strong>Payment records:</strong> Invoice and payment history retained for 7 years
                  as required by Canadian tax law.
                </li>
              </ul>
            </section>

            <Separator />

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">7. Your Rights Under PIPA</h2>
              <p>
                Under the BC <em>Personal Information Protection Act</em>, you have the right to:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li><strong>Access:</strong> Request a copy of personal information we hold about you</li>
                <li><strong>Correction:</strong> Request that inaccurate or incomplete information be corrected</li>
                <li><strong>Withdrawal of consent:</strong> Withdraw consent for collection or use where consent is the basis for processing (note: some processing is required to provide the Service)</li>
                <li><strong>Deletion (right to erasure):</strong> Request deletion of your personal information, subject to our legal retention obligations</li>
                <li><strong>Complaint:</strong> File a complaint with the BC Office of the Information and Privacy Commissioner (OIPC) at <a href="https://www.oipc.bc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline">oipc.bc.ca</a></li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, email us at{" "}
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary underline">{PRIVACY_EMAIL}</a>.
                We will respond within 30 days. We may ask you to verify your identity before
                processing your request.
              </p>
            </section>

            <Separator />

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">8. Cross-Border Data Transfers</h2>
              <p>
                Our sub-processors (including Supabase, Stripe, Resend, PostHog, and Sentry)
                operate servers primarily in the United States. By using the Service, you consent
                to your personal information being transferred to and processed in the United States,
                which may have different privacy laws than Canada.
              </p>
              <p className="mt-3">
                We require our sub-processors to maintain appropriate safeguards for personal
                information, including contractual data processing agreements. The BC Registry API
                is operated within Canada.
              </p>
            </section>

            <Separator />

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">9. Security</h2>
              <p>
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>TLS encryption for all data in transit</li>
                <li>AES-256 encryption for all data at rest (via AWS S3 and Supabase)</li>
                <li>Row-Level Security (RLS) policies ensuring organizations can only access their own data</li>
                <li>No storage of raw payment card data (tokenized by Stripe)</li>
                <li>Regular dependency vulnerability reviews</li>
              </ul>
              <p className="mt-3">
                Despite these measures, no internet-based system is 100% secure.
                In the event of a privacy breach that poses a real risk of significant harm,
                we will notify affected individuals and the BC OIPC as required by PIPA.
              </p>
            </section>

            <Separator />

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">10. Children's Privacy</h2>
              <p>
                The Service is not directed to individuals under the age of 19.
                We do not knowingly collect personal information from minors.
                If you believe a minor has provided us with personal information,
                please contact us at{" "}
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary underline">{PRIVACY_EMAIL}</a>{" "}
                and we will delete it promptly.
              </p>
            </section>

            <Separator />

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of
                material changes by posting the new policy on this page and, where appropriate,
                by email or in-app notification at least 14 days before the change takes effect.
                Your continued use of the Service after the change constitutes acceptance of
                the updated policy.
              </p>
            </section>

            <Separator />

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">12. Contact Us</h2>
              <p>
                For privacy inquiries, access requests, or complaints:
              </p>
              <address className="mt-3 not-italic space-y-1 text-muted-foreground">
                <p><strong className="text-foreground">Privacy Officer — {COMPANY_NAME}</strong></p>
                <p>British Columbia, Canada</p>
                <p>
                  <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary underline">
                    {PRIVACY_EMAIL}
                  </a>
                </p>
                <p className="mt-2 text-xs">
                  BC Office of the Information &amp; Privacy Commissioner (OIPC):{" "}
                  <a href="https://www.oipc.bc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    www.oipc.bc.ca
                  </a>
                </p>
              </address>
            </section>

          </div>

          {/* Back link */}
          <div className="mt-12 flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/terms">
                View Terms of Service →
              </Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/login">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back to app
              </Link>
            </Button>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
